import React, { useCallback, useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, PackageCheck, FileUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PurchaseSuggestions } from "@/components/PurchaseSuggestions";
import { NfeImportDialog } from "@/components/NfeImportDialog";

const EMPTY = { supplier_id: "", supplier_name: "", items: [], freight: 0, discount: 0, due_date: "", notes: "", nfe_key: "", status: "received" };
const fmtKey = (k) => (k || "").replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ");

export default function Purchases() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [addProductId, setAddProductId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [nfeOpen, setNfeOpen] = useState(false);

  const load = useCallback(() => {
    setReloadKey((k) => k + 1);
    api.get("/purchases").then((r) => setItems(r.data));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.type === "product")));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setAddProductId(""); setOpen(true); };
  const openFromSuggestions = (list) => {
    setForm({ ...EMPTY, status: "pending", items: list.map((i) => ({ product_id: i.product_id, name: i.name, quantity: i.suggested_qty, cost_price: i.cost_price || 0 })) });
    setAddProductId(""); setOpen(true);
  };

  const receive = async (id) => {
    if (!confirm("Confirmar recebimento? Os itens entrarão no estoque.")) return;
    try { await api.post(`/purchases/${id}/receive`); toast.success("Compra recebida e estoque atualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const addItem = () => {
    const p = products.find((x) => x.id === addProductId);
    if (!p) return;
    if (form.items.find((i) => i.product_id === p.id)) return toast.error("Produto já adicionado");
    setForm((f) => ({ ...f, items: [...f.items, { product_id: p.id, name: p.name, quantity: 1, cost_price: p.cost_price || 0 }] }));
    setAddProductId("");
  };

  const updateItem = (idx, key, value) => {
    setForm((f) => ({ ...f, items: f.items.map((i, j) => j === idx ? { ...i, [key]: value } : i) }));
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + Number(i.quantity) * Number(i.cost_price), 0);
  const total = Math.max(0, subtotal + Number(form.freight || 0) - Number(form.discount || 0));

  const save = async () => {
    if (!form.items.length) return toast.error("Adicione pelo menos 1 item");
    const keyDigits = (form.nfe_key || "").replace(/\D/g, "");
    if (keyDigits && keyDigits.length !== 44) return toast.error("A chave da NF-e deve ter 44 dígitos");
    try {
      const payload = {
        supplier_id: form.supplier_id || null,
        supplier_name: form.supplier_name || (suppliers.find((s) => s.id === form.supplier_id)?.name) || null,
        items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), cost_price: Number(i.cost_price) })),
        freight: Number(form.freight || 0),
        discount: Number(form.discount || 0),
        due_date: form.due_date || null,
        notes: form.notes || null,
        nfe_key: keyDigits || null,
        status: form.status,
      };
      await api.post("/purchases", payload);
      setOpen(false);
      toast.success(form.status === "received" ? "Compra registrada e estoque atualizado" : "Pedido registrado — confirme o recebimento para atualizar o estoque");
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir esta compra? O estoque será revertido.")) return;
    const { data } = await api.delete(`/purchases/${id}`);
    toast.success(data.message || "Compra excluída"); load();
  };

  const totalMonth = items.filter((p) => new Date(p.created_at).getMonth() === new Date().getMonth()).reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Compras</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Painel de Compras</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setNfeOpen(true)} data-testid="import-nfe-btn">
            <FileUp className="w-4 h-4 mr-2" /> Importar XML da NF-e
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-purchase-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Compra
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Compras no mês</div>
          <div className="kpi-num text-3xl mt-2 text-blue-600">{brl(totalMonth)}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Nº de compras</div>
          <div className="kpi-num text-3xl mt-2">{items.length}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Fornecedores ativos</div>
          <div className="kpi-num text-3xl mt-2">{suppliers.length}</div>
        </CardContent></Card>
      </div>

      <PurchaseSuggestions reloadKey={reloadKey} onCreate={openFromSuggestions} />

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Fornecedor</TableHead><TableHead>NF-e</TableHead>
              <TableHead>Itens</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead>Vencimento</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                  <TableCell className="text-sm">{dateBR(p.created_at)}</TableCell>
                  <TableCell className="font-medium">{p.supplier_name || "—"}</TableCell>
                  <TableCell className="text-xs font-mono" title={fmtKey(p.nfe_key)}>{p.nfe_key ? `Nº ${Number(p.nfe_key.slice(25, 34))}` : "—"}</TableCell>
                  <TableCell><Badge variant="outline">{p.items.length} itens</Badge></TableCell>
                  <TableCell className="text-right kpi-num text-emerald-600">{brl(p.total)}</TableCell>
                  <TableCell className="text-sm">{p.due_date || "À vista"}</TableCell>
                  <TableCell>
                    {p.status === "pending" ? <Badge className="bg-amber-500">A receber</Badge> : <Badge className="bg-emerald-600">Recebida</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {p.status === "pending" && (
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => receive(p.id)} data-testid={`receive-pur-${p.id}`}>
                        <PackageCheck className="w-3.5 h-3.5 mr-1" /> Receber
                      </Button>
                    )}
                    {user?.role === "admin" && (
                      <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(p.id)} data-testid={`del-pur-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Nenhuma compra registrada.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NfeImportDialog open={nfeOpen} onClose={() => setNfeOpen(false)} onDone={() => setTimeout(load, 350)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle className="font-display">Nova Compra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Fornecedor</Label>
                <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="pur-supplier"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Avulso</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Vencimento (opcional, cria conta a pagar)</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="pur-due" />
              </div>
              <div><Label>Chave de acesso da NF-e (opcional)</Label>
                <Input value={fmtKey(form.nfe_key)} onChange={(e) => setForm({ ...form, nfe_key: e.target.value.replace(/\D/g, "").slice(0, 44) })}
                  placeholder="44 dígitos" className="font-mono text-xs" inputMode="numeric" data-testid="pur-nfe-key" />
                <div className="text-[11px] text-slate-500 mt-1">{(form.nfe_key || "").length}/44 dígitos</div>
              </div>
              <div><Label>Situação</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="pur-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Já recebida (entra no estoque agora)</SelectItem>
                    <SelectItem value="pending">A receber (entra no estoque ao confirmar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Adicionar produto</Label>
              <div className="flex gap-2">
                <Select value={addProductId} onValueChange={setAddProductId}>
                  <SelectTrigger className="flex-1" data-testid="pur-add-product"><SelectValue placeholder="Selecione um produto" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={addItem} disabled={!addProductId} data-testid="pur-add-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {form.items.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead className="w-24 text-right">Qtd</TableHead>
                    <TableHead className="w-32 text-right">Custo R$</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => (
                      <TableRow key={it.product_id}>
                        <TableCell className="text-sm">{it.name}</TableCell>
                        <TableCell><Input type="number" step="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.cost_price} onChange={(e) => updateItem(idx, "cost_price", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell className="text-right kpi-num">{brl(Number(it.quantity) * Number(it.cost_price))}</TableCell>
                        <TableCell><Button size="icon" variant="ghost" className="text-rose-600 h-7 w-7" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Frete</Label>
                <Input type="number" step="0.01" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} data-testid="pur-freight" /></div>
              <div><Label>Desconto</Label>
                <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="pur-discount" /></div>
              <div className="flex flex-col justify-end">
                <div className="text-xs text-slate-500">Total</div>
                <div className="kpi-num text-2xl text-blue-600">{brl(total)}</div>
              </div>
            </div>
            <Textarea placeholder="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-purchase-btn">{form.status === "pending" ? "Registrar pedido" : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
